/*
    Consent-gated Google Analytics.

    gtag.js used to be loaded unconditionally from public/index.html, which set
    the _ga cookies on every visitor before they had agreed to anything. It is
    now injected from here and only while analytics consent is granted, as
    promised by the privacy policy on the homepage.

    The consent decision itself lives in public/js/cookie-consent.js (loaded
    before the bundle, exposing window.underwaterlyConsent) and is stored in a
    cookie on .underwaterly.com, so a visitor who decided on the homepage is
    not asked again here.

    Usage: call initAnalytics() once at startup (see src/index.js). It
    subscribes to the consent store and reacts to every later change too, so
    withdrawing consent from the cookie settings takes effect immediately,
    without a reload.
*/

const MEASUREMENT_ID = "G-0K6CX8LDL9";

// Setting window["ga-disable-<id>"] is Google's own opt-out switch: gtag.js
// checks it on every call, so it also stops a copy that is already running.
const DISABLE_FLAG = `ga-disable-${MEASUREMENT_ID}`;

let gtagLoaded = false;

// gtag.js reads the `arguments` object off the queued items, so this has to
// stay a plain function - an arrow function or rest parameters would push a
// shape that gtag.js does not understand.
function gtag() {
  window.dataLayer.push(arguments);
}

function loadGtag() {
  window.dataLayer = window.dataLayer || [];
  window.gtag = gtag;

  // Consent Mode: declare the default as denied before configuring the
  // measurement ID, so that granting and withdrawing later are both plain
  // 'update' calls on a tag that never assumed permission.
  gtag("consent", "default", { analytics_storage: "denied" });
  gtag("js", new Date());
  gtag("config", MEASUREMENT_ID);

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  gtagLoaded = true;
}

// Remove the cookies gtag.js sets (_ga and the per-property _ga_<container>).
// A cookie can only be deleted by an exact name/path/domain match, and we do
// not know which of the two domains a given one was set on - gtag.js uses
// .underwaterly.com in production but the bare host on localhost - so try
// both. This also clears the _ga cookies that visitors were given while
// gtag.js still loaded unconditionally.
function deleteAnalyticsCookies() {
  const hostname = window.location.hostname;
  const domains = [null, hostname];
  if (hostname === "underwaterly.com" || hostname.endsWith(".underwaterly.com")) {
    domains.push(".underwaterly.com");
  }

  document.cookie.split("; ").forEach((entry) => {
    const name = entry.split("=")[0];
    if (name !== "_ga" && !name.startsWith("_ga_")) return;

    domains.forEach((domain) => {
      document.cookie =
        `${name}=; path=/; max-age=0` + (domain ? `; domain=${domain}` : "");
    });
  });
}

function grantAnalytics() {
  window[DISABLE_FLAG] = false;
  if (!gtagLoaded) loadGtag();
  gtag("consent", "update", { analytics_storage: "granted" });
}

function denyAnalytics() {
  window[DISABLE_FLAG] = true;
  if (gtagLoaded) gtag("consent", "update", { analytics_storage: "denied" });
  deleteAnalyticsCookies();
}

export default function initAnalytics() {
  const consentStore = window.underwaterlyConsent;

  // The consent store failed to load (blocked by an extension, offline, ...).
  // Without a decision we cannot load analytics, so do nothing.
  if (!consentStore) return undefined;

  // onChange fires once immediately with the current decision (or null while
  // the visitor has not decided yet) and again on every change afterwards.
  return consentStore.onChange((consent) => {
    if (consent && consent.analytics) grantAnalytics();
    else denyAnalytics();
  });
}
